import { productSubcategories } from "@aclymatepackages/lists";

import {
  faTriangleExclamation,
  faQuestion,
  faClockRotateLeft,
  faCircleCheck,
  faBarcode,
  faBoxOpen,
  faIndustry
} from "@fortawesome/free-solid-svg-icons";

import { isObjectEmpty } from "../otherHelpers";

export const getProductSubcategoryElectonicDefaultWeightPercentage = (
  subcategory
) => {
  const productSubcategory = productSubcategories.find(
    ({ id }) => id === subcategory
  );

  if (!productSubcategory) {
    return 0;
  }

  const { isElectronic, defaults } = productSubcategory;

  if (!isElectronic) {
    return 0;
  }

  const { electronicWeightPercentage = 0 } = defaults || {};
  return electronicWeightPercentage;
};

export const calculateMaterialsInputWeightsSum = (
  materials,
  totalMaterialsWeight
) => {
  const adjustedWeights = materials.map(
    ({ weight, defaultWeight, weightUnit }) =>
      weightUnit === "%"
        ? totalMaterialsWeight * ((weight || defaultWeight) / 100)
        : weight || defaultWeight
  );
  return adjustedWeights.reduce((sum, weight = 0) => sum + Number(weight), 0);
};

const areProductWeightsValid = (materials, totalMaterialsWeight) => {
  const percentageWeights = materials.filter(
    ({ weightUnit }) => weightUnit === "%"
  );
  if (percentageWeights.some(({ weight }) => weight > 100)) {
    return false;
  }

  const adjustedWeightsSum = calculateMaterialsInputWeightsSum(
    materials,
    totalMaterialsWeight
  );

  if (Math.round(adjustedWeightsSum) !== Math.round(totalMaterialsWeight)) {
    return false;
  }

  return true;
};

export const areMaterialsValid = (materials, totalMaterialsWeight) => {
  if (
    !materials.every(
      ({ type, weight, defaultWeight }) => type && (weight || defaultWeight)
    )
  ) {
    return false;
  }

  return areProductWeightsValid(materials, totalMaterialsWeight);
};

export const isRouteComplete = (route) => {
  if (!route) {
    return false;
  }
  const { origin, destination, mode, destinations = [], legs = [] } = route;
  if (origin || destination || mode) {
    return !!(origin && destination && mode);
  }
  if (!destinations.length || !legs?.length) {
    return false;
  }

  return (
    destinations.every((dest) => !isObjectEmpty(dest)) &&
    legs.every(({ transportationMethod }) => transportationMethod)
  );
};

export const areComponentsValid = (components = [], totalProductWeight) => {
  if (!components.length) {
    return false;
  }

  const everyComponentHasName = components.every(
    ({ name }) => typeof name === 'string' && name.trim().length > 0
  );
  if (!everyComponentHasName) {
    return false;
  }

  const everyComponentHasValidMaterials = components.every(
    ({ materials = [], productWeight }) =>
      areMaterialsValid(materials, productWeight)
  );
  if (!everyComponentHasValidMaterials) {
    return false;
  }

  const componentWeightSum = components.reduce(
    (sum, { productWeight = 0 }) => sum + Number(productWeight),
    0
  );
  return Math.round(componentWeightSum) === Math.round(totalProductWeight);
};

export const isProductBomComplete = ({
  components = [],
  productWeight,
  packaging = [],
  packagingWeight,
  route = {},
  subcategory
}) =>
  areComponentsValid(components, productWeight) &&
  areMaterialsValid(packaging, packagingWeight) &&
  isRouteComplete(route) &&
  !!subcategory;

// Mirrors resolveOwnerId in functions/controllers/products.js: the owner is
// creatorId, or for an older product without one, its sole buyerIds member.
// Only the owner can share a product with its customers; a product this
// account only received from its own supplier isn't this account's to share.
export const isProductOwnedBy = ({ creatorId, buyerIds = [] }, accountId) =>
  creatorId
    ? creatorId === accountId
    : buyerIds.length === 1 && buyerIds[0] === accountId;

// Simulation and non-company view modes keep every cached-CRUD and
// relationship write local (see useCachedFirebaseCrud and
// useUpdateVendorRelationshipFields). The product access syncs write through
// the Admin SDK, so callers skip them in these modes too.
export const isLocalOnlyMode = ({ viewMode, isSimulation } = {}) =>
  !!isSimulation || (!!viewMode && viewMode !== "company");

export const isProductLinkedToVendor = (
  product,
  vendor,
  productLifecycleEvents
) => {
  const lifecycle = productLifecycleEvents.find(
    ({ productId }) => productId === product.id
  );
  return lifecycle?.vendor?.id === vendor.vendorId;
};

const buyerProductStatusMap = {
  confirmed: {
    tooltip: "BOM confirmed by vendor",
    icon: faCircleCheck,
    colorKey: "secondary"
  },
  requested: {
    tooltip: "Awaiting vendor BOM data",
    icon: faClockRotateLeft,
    colorKey: "warning"
  }
};

const vendorProductStatusMap = {
  confirmed: {
    tooltip: "You have confirmed this BOM",
    icon: faCircleCheck,
    colorKey: "secondary"
  },
  requested: {
    tooltip: "Customer is requesting your BOM data",
    icon: faClockRotateLeft,
    colorKey: "warning"
  },
  pending: {
    tooltip: "Customer assigned you as the vendor — fill in the BOM data",
    icon: faClockRotateLeft,
    colorKey: "warning"
  }
};

export const getProductStatus = ({ kgsCo2e, bomStatus, ownerKind }) => {
  const isVendorView = ownerKind === "customer";
  const statusMap = isVendorView
    ? vendorProductStatusMap
    : buyerProductStatusMap;
  if (statusMap[bomStatus]) {
    return statusMap[bomStatus];
  }
  if (!kgsCo2e) {
    return {
      tooltip: "Incomplete — product has no footprint data yet",
      icon: faTriangleExclamation,
      colorKey: "error"
    };
  }
  return {
    tooltip: isVendorView
      ? "Unconfirmed — fill in the BOM and share it on the customer's Data Sharing tab"
      : "Unconfirmed — footprint has not been validated by the vendor",
    icon: faQuestion,
    colorKey: "warning"
  };
};

export const bomProductsFieldOptions = [
  {
    label: "SKU",
    value: "sku",
    type: "string",
    description: "A unique identifier for the product",
    icon: faBarcode,
    required: true
  },
  {
    label: "Product Name",
    value: "name",
    type: "string",
    description: "The name or description of the product",
    icon: faBoxOpen,
    required: true
  },
  {
    label: "Supplier Name",
    value: "supplierName",
    type: "string",
    description: "The vendor or supplier of this product",
    icon: faIndustry,
    required: true
  },
  { label: "Supplier Code", value: "supplierCode", type: "string" },
  { label: "Supplier Address", value: "supplierAddress", type: "string" },
  { label: "Factory Code", value: "factoryCode", type: "string" },
  { label: "Factory Address", value: "factoryAddress", type: "string" },
  { label: "Contact Name", value: "contactName", type: "string" },
  { label: "Contact Email", value: "contactEmail", type: "string" },
  { label: "Secondary Contact", value: "secondaryContact", type: "string" },
  { label: "Category", value: "category", type: "string" },
  { label: "Subcategory", value: "subcategory", type: "string" }
];

export const isReportSummaryUsable = (reportSummary) =>
  Boolean(reportSummary) && !reportSummary.error;

export const safeFetchReportSummary = async ({
  fetchReportSummary,
  payload,
  onError
}) => {
  try {
    return await fetchReportSummary(payload);
  } catch (fetchError) {
    if (onError) {
      onError(fetchError);
    }
    return null;
  }
};

export const resolveReportSummarySafely = async ({
  product,
  lifecycleEvent,
  emissionsFactors,
  calculateFootprint,
  fetchReportSummary,
  onError
}) => {
  if (!emissionsFactors) {
    return null;
  }
  try {
    const { kgsCo2e, footprintBreakdownKgs } = calculateFootprint(
      { ...product, ...lifecycleEvent },
      emissionsFactors
    );
    return await safeFetchReportSummary({
      fetchReportSummary,
      payload: { ...product, ...lifecycleEvent, kgsCo2e, footprintBreakdownKgs },
      onError
    });
  } catch (summaryError) {
    if (onError) {
      onError(summaryError);
    }
    return null;
  }
};

export const saveProductWithComponents = async ({
  components = [],
  buildComponentDoc,
  buildMergedComponents,
  productDocObj,
  productLifecycleEventDoc,
  fetchReportSummary,
  newCollectionDoc,
  updateCollectionDoc,
  onError
}) => {
  const archiveOrphans = async ({ parentId, childIds, lifecycleEventId }) => {
    const componentIdsToArchive = [parentId, ...childIds].filter(Boolean);
    await Promise.allSettled([
      ...componentIdsToArchive.map((id) =>
        updateCollectionDoc("product-components", id, { archived: true })
      ),
      ...(lifecycleEventId
        ? [
            updateCollectionDoc("product-lifecycle-events", lifecycleEventId, {
              archived: true
            })
          ]
        : [])
    ]);
  };

  const componentIds = [];
  let productId = null;
  let lifecycleEventId = null;
  try {
    const childResults = await Promise.allSettled(
      components.map((comp) =>
        newCollectionDoc("product-components", buildComponentDoc(comp))
      )
    );
    componentIds.push(
      ...childResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value)
    );

    const rejectedChildren = childResults.filter(
      (result) => result.status === "rejected"
    );
    if (rejectedChildren.length) {
      const componentWriteErrors = rejectedChildren.map(
        (result) => result.reason
      );
      const combinedError = new Error(
        componentWriteErrors.length > 1
          ? `${componentWriteErrors.length} component writes failed: ${componentWriteErrors
              .map((reason) => reason?.message || reason)
              .join("; ")}`
          : componentWriteErrors[0]?.message || "Component write failed"
      );
      combinedError.componentWriteErrors = componentWriteErrors;
      throw combinedError;
    }

    const componentRefs = componentIds.map((cId) => ({ id: cId }));
    const mergedProduct = {
      ...productDocObj,
      componentRefs,
      components: buildMergedComponents(components)
    };

    const reportSummary = await fetchReportSummary(
      mergedProduct,
      productLifecycleEventDoc
    );

    productId = await newCollectionDoc("product-components", {
      ...productDocObj,
      componentRefs
    });

    const [linkSettlement, lifecycleSettlement] = await Promise.allSettled([
      Promise.all(
        componentIds.map((cId) =>
          updateCollectionDoc("product-components", cId, {
            parentProductId: productId
          })
        )
      ),
      newCollectionDoc("product-lifecycle-events", {
        ...productLifecycleEventDoc,
        productId,
        ...(isReportSummaryUsable(reportSummary) ? { reportSummary } : {})
      })
    ]);

    if (lifecycleSettlement.status === "fulfilled") {
      lifecycleEventId = lifecycleSettlement.value;
    }
    if (linkSettlement.status === "rejected") {
      throw linkSettlement.reason;
    }
    if (lifecycleSettlement.status === "rejected") {
      throw lifecycleSettlement.reason;
    }

    return { productId, componentIds, lifecycleEventId };
  } catch (err) {
    if (onError) {
      onError(err, { productId, componentIds, lifecycleEventId });
    }
    await archiveOrphans({
      parentId: productId,
      childIds: componentIds,
      lifecycleEventId
    });
    throw err;
  }
};

// A failed factors fetch leaves the context on empty tables, which makes every
// footprint calculate as 0 and read as "add components" rather than as an error.
export const areProductFactorsUnavailable = (emissionsFactors, isLoading) =>
  !isLoading && !(emissionsFactors?.materials || []).length;

// The product calculation returns packaging rows in the same order they were
// given, but without their original weight/unit. Pair each calculated row with
// its own original row: by position when the lists line up, otherwise by type
// and label. Matching on type alone gave every row of a shared type (several
// "paper" rows) the first one's weight.
export const restoreOriginalWeights = (calculatedItems = [], originalItems = []) => {
  const sameLength = calculatedItems.length === originalItems.length;
  return calculatedItems.map((calcItem, index) => {
    const atSameIndex =
      sameLength && originalItems[index]?.type === calcItem.type
        ? originalItems[index]
        : null;
    const originalItem =
      atSameIndex ||
      originalItems.find(
        ({ type, label }) => type === calcItem.type && label === calcItem.label
      ) ||
      originalItems.find(({ type }) => type === calcItem.type);
    return {
      ...calcItem,
      ...(originalItem
        ? { weight: originalItem.weight, weightUnit: originalItem.weightUnit }
        : {})
    };
  });
};
